import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import { ContentNugget } from '@backrs_ui/common-ui';

import { fetchContentNuggetsByCompetencyId } from '@backrs_ui/grow-ui/src/redux/contentNugget/thunks/nuggetsByCompetency';

import {
  fetchOneContentNuggetBySlug,
  searchForCareerPathById,
  searchForContentScholarship,
  searchForContentInternship,
  searchForProfileInternalById,
  searchForPostById,
  searchForContentProfileExternalById,
  searchForGeneratedContentByUuid,
} from './thunks/oneNuggetBySlug';
import { logout } from '../session/logoutThunk';
import { RootState } from '../store';

type ContentNuggetState = Readonly<{
  currentNuggetList: ContentNugget[];
  // preFetchedNugget?: ContentNugget;
  currentDialogNugget?: ContentNugget;
  loading: boolean;
  error?: string;
}>;

const initialState: ContentNuggetState = {
  currentNuggetList: [],
  loading: false,
};

export const contentNuggetSlice = createSlice({
  name: 'contentNugget',
  initialState,
  reducers: {
    setCurrentDialogNugget: (state, action: PayloadAction<ContentNugget | undefined>) => {
      state.currentDialogNugget = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContentNuggetsByCompetencyId.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(
        fetchContentNuggetsByCompetencyId.fulfilled,
        (state, action: PayloadAction<ContentNugget[]>) => {
          state.currentNuggetList = action.payload;
          state.loading = false;
        },
      )
      .addCase(fetchContentNuggetsByCompetencyId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // for pre-fetching a single nugget that can be loaded into the Dashboard page
      .addCase(fetchOneContentNuggetBySlug.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(
        fetchOneContentNuggetBySlug.fulfilled,
        (state, action: PayloadAction<ContentNugget>) => {
          state.currentDialogNugget = action.payload;
          state.loading = false;
        },
      )
      .addCase(fetchOneContentNuggetBySlug.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // for pre-fetching a single scholarship that can be loaded into the Dashboard page
      .addCase(searchForContentScholarship.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(
        searchForContentScholarship.fulfilled,
        (state, action: PayloadAction<ContentNugget>) => {
          state.currentDialogNugget = action.payload;
          state.loading = false;
        },
      )
      .addCase(searchForContentScholarship.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // for pre-fetching a single internship that can be loaded into the Dashboard page
      .addCase(searchForContentInternship.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(
        searchForContentInternship.fulfilled,
        (state, action: PayloadAction<ContentNugget>) => {
          state.currentDialogNugget = action.payload;
          state.loading = false;
        },
      )
      .addCase(searchForContentInternship.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // for pre-fetching a single career path that can be loaded into the Dashboard page
      .addCase(searchForCareerPathById.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(searchForCareerPathById.fulfilled, (state, action: PayloadAction<ContentNugget>) => {
        state.currentDialogNugget = action.payload;
        state.loading = false;
      })
      .addCase(searchForCareerPathById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // for pre-fetching a single profile internal that can be loaded into the Dashboard page
      .addCase(searchForProfileInternalById.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(
        searchForProfileInternalById.fulfilled,
        (state, action: PayloadAction<ContentNugget>) => {
          state.currentDialogNugget = action.payload;
          state.loading = false;
        },
      )
      .addCase(searchForProfileInternalById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // for pre-fetching a single profile external that can be loaded into the Dashboard page
      .addCase(searchForContentProfileExternalById.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(
        searchForContentProfileExternalById.fulfilled,
        (state, action: PayloadAction<ContentNugget>) => {
          state.currentDialogNugget = action.payload;
          state.loading = false;
        },
      )
      .addCase(searchForContentProfileExternalById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // searchForPostById
      .addCase(searchForPostById.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(searchForPostById.fulfilled, (state, action: PayloadAction<ContentNugget>) => {
        state.currentDialogNugget = action.payload;
        state.loading = false;
      })
      .addCase(searchForPostById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // for pre-fetching a single generated-content record that can be loaded into the Dashboard page
      .addCase(searchForGeneratedContentByUuid.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(
        searchForGeneratedContentByUuid.fulfilled,
        (state, action: PayloadAction<ContentNugget>) => {
          state.currentDialogNugget = action.payload;
          state.loading = false;
        },
      )
      .addCase(searchForGeneratedContentByUuid.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // LOGOUT
      .addCase(logout, (state) => {
        state.currentNuggetList = [];
        state.loading = false;
        state.error = undefined;
      });
  },
});

// Other code such as selectors can use the imported `RootState` type
export const selectContentNuggets = (state: RootState) => state.contentNugget;

export const contentNuggetReducer = contentNuggetSlice.reducer;

export const { setCurrentDialogNugget } = contentNuggetSlice.actions;
